import { values } from 'lodash';
import { Dimension, ExtendedValence } from '../../types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';

/**
 * Taxonomy information for a single impact,
 * e.g. "treats diseases"
 */
export interface ImpactTaxonomyDatum {
  phrases: string[];
  productMappings: string[];
  code: string;
  title: string;
  valence: ExtendedValence;
}

/**
 * Taxonomy information for a single impact category
 * e.g. "diseases"
 */
export interface ImpactCategory {
  impacts: ImpactTaxonomyDatum[];
  title: string;
  code: string;
}

export type ByImpactCategory<T> = {
  [impact in ImpactCategoryId]: T;
};

export type BySomeImpactCategories<T> = {
  [impact in ImpactCategoryId]?: T;
};

export const allImpactCodes = values(ImpactCategoryId);
export const allImpactCodesStr = allImpactCodes.map(code => `${code}`);

export function toImpactCategoryId(value: string): ImpactCategoryId {
  const match = Object.values(ImpactCategoryId).find(id => value === id.toString());
  if (!match) {
    throw Error(`Unable to parse impact category id ${value}`);
  }
  return match;
}
export function isImpactCategoryId(value: string | undefined): value is ImpactCategoryId {
  return value !== undefined && (allImpactCodes as string[]).includes(value);
}

export function getDimensionOfImpactCategory(impactCategoryId: ImpactCategoryId): Dimension {
  switch (impactCategoryId) {
    case ImpactCategoryId.JOBS:
    case ImpactCategoryId.TAXES:
    case ImpactCategoryId.SOCIETAL_INFRA:
    case ImpactCategoryId.SOCIETAL_STABILITY:
    case ImpactCategoryId.EQUALITY:
      return Dimension.SOCIAL;
    case ImpactCategoryId.KNOWLEDGE_INFRA:
    case ImpactCategoryId.CREATES_KNOWLEDGE:
    case ImpactCategoryId.DISTRIBUTING_KNOWLEDGE:
    case ImpactCategoryId.SCARCE_HUMAN_CAPITAL:
      return Dimension.KNOWLEDGE;
    case ImpactCategoryId.PHYSICAL_DISEASES:
    case ImpactCategoryId.MENTAL_DISEASES:
    case ImpactCategoryId.NUTRITION:
    case ImpactCategoryId.RELATIONSHIPS:
    case ImpactCategoryId.MEANING_AND_JOY:
      return Dimension.HEALTH;
    case ImpactCategoryId.GHG_EMISSIONS:
    case ImpactCategoryId.NON_GHG_EMISSIONS:
    case ImpactCategoryId.SCARCE_NATURAL_RESOURCES:
    case ImpactCategoryId.BIODIVERSITY:
    case ImpactCategoryId.WASTE:
      return Dimension.ENVIRONMENT;
  }
}

export interface UIImpactCategory {
  code: string;
  title: string;
  dimension: Dimension;
  explanations: ImpactCategoryExplanations;
}

export interface UIImpactCategoryByCode {
  [code: string]: UIImpactCategory;
}

export interface ImpactCategoryExplanations {
  impactCategoryCode: string;
  topicsConsidered?: string[];
  explanationsAndExamplesByValence: {
    negativeExplanation?: string;
    negativeExample?: string;
    positiveExplanation?: string;
    positiveExample?: string;
  };
  productViewExplanation?: string;
}

export interface ImpactCategoryExplanationsByImpactCode {
  [code: string]: ImpactCategoryExplanations;
}

export interface CommonImpactDatumProperties {
  lastModifiedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImpactLabelDatum extends CommonImpactDatumProperties {
  id: string; // impact id
  impactCode: string; // impact category code
  extendedValence: ExtendedValence;
  title: string;
  phrases: ImpactLabelPhraseDatum[];
  products: ImpactLabelProductDatum[];
}

export interface ImpactLabelPhraseDatum extends CommonImpactDatumProperties {
  impactId: string;
  phrase: string;
}

export enum ImpactLabelProductType {
  BCA_WHITELIST = 'bcaw',
  BSF_WHITELIST = 'bsfw',
}

export interface ImpactLabelProductDatum extends CommonImpactDatumProperties {
  impactId: string;
  productCode: string;
  type: ImpactLabelProductType;
}

export interface ImpactLabelById {
  [id: string]: ImpactLabelDatum;
}

/*
 * Impact labels by impactCode for an impact category
 */
export interface ImpactLabelsByImpactCode {
  [code: string]: ImpactLabelDatum[];
}

export interface ImpactNamesByLang {
  [lang: string]: ImpactNameByCode;
}

export interface ImpactNameByCode {
  [impactCode: string]: string;
}
